import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Switch, TextField, Typography, FormControlLabel, List, ListItem, ListItemText } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import util from '../util';
import { formatBytes } from '../uploads/UploadsContext';
import { setParentID } from './CDN';

const SearchPage = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);

    const [sortBy, setSortBy] = useState(Number(localStorage.getItem('cdnSearchSortBy') || 0));
    const [sortOrder, setSortOrder] = useState(Number(localStorage.getItem('cdnSearchSortOrder') || -1));
    const [fullLoad, setFullLoad] = useState(localStorage.getItem('cdnSearchFullLoad') === 'true');

    const pageSize = 100;

    async function performSearch(reset = true) {
        const trimmed = query.trim();
        if (!trimmed) {
            setResults([]);
            setSearched(false);
            return;
        }

        setLoading(true);
        try {
            const nextOffset = reset ? 0 : offset;
            const response = await util.postRequest('/api/content/search', {
                query: trimmed,
                offset: nextOffset,
                limit: fullLoad ? 10000 : pageSize,
                sort: sortBy,
                order: sortOrder,
                fullLoad
            });

            if (reset) {
                setResults(response.data || []);
            } else {
                const merged = [...results, ...(response.data || [])];
                setResults(merged.filter((item, index) => merged.findIndex(x => x._id === item._id) === index));
            }

            const loadedCount = (reset ? 0 : offset) + (response.data?.length || 0);
            setOffset(loadedCount);
            setTotal(response.total || 0);
            setHasMore(!!response.hasMore);
            setSearched(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (searched) {
            performSearch(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, sortOrder, fullLoad]);

    const emptyText = useMemo(() => {
        if (!searched) return 'Run a search to see results.';
        if (loading) return 'Searching...';
        return 'No matching files or folders.';
    }, [searched, loading]);

    return (
        <Box sx={{ mt: 2, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => navigate('/contentManager')}>Back</Button>
                <TextField
                    label="Search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ minWidth: 320 }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            performSearch(true);
                        }
                    }}
                />
                <Button variant="contained" onClick={() => performSearch(true)}>Search</Button>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="search-sort-by">Sort By</InputLabel>
                    <Select
                        labelId="search-sort-by"
                        label="Sort By"
                        value={sortBy}
                        onChange={(e) => {
                            const next = Number(e.target.value);
                            setSortBy(next);
                            localStorage.setItem('cdnSearchSortBy', String(next));
                        }}
                    >
                        <MenuItem value={0}>Updated</MenuItem>
                        <MenuItem value={1}>Name</MenuItem>
                        <MenuItem value={2}>Size</MenuItem>
                        <MenuItem value={3}>Type</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="search-sort-order">Order</InputLabel>
                    <Select
                        labelId="search-sort-order"
                        label="Order"
                        value={sortOrder}
                        onChange={(e) => {
                            const next = Number(e.target.value);
                            setSortOrder(next);
                            localStorage.setItem('cdnSearchSortOrder', String(next));
                        }}
                    >
                        <MenuItem value={-1}>Descending</MenuItem>
                        <MenuItem value={1}>Ascending</MenuItem>
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={<Switch checked={fullLoad} onChange={(e) => {
                        const enabled = e.target.checked;
                        setFullLoad(enabled);
                        localStorage.setItem('cdnSearchFullLoad', String(enabled));
                    }} />}
                    label="Always full load"
                />
            </Box>

            <Typography sx={{ mb: 1, opacity: 0.8 }}>
                {searched ? `${results.length}/${total} result(s)` : 'Search across your files and folders'}
            </Typography>

            <Box sx={{ backgroundColor: '#191919', borderRadius: 1, minHeight: 260, p: 1 }}>
                {loading && results.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : results.length === 0 ? (
                    <Typography sx={{ py: 6, textAlign: 'center', opacity: 0.75 }}>{emptyText}</Typography>
                ) : (
                    <List>
                        {results.map((item) => (
                            <ListItem
                                key={item._id}
                                secondaryAction={
                                    <Button size="small" onClick={() => {
                                        console.log(item)
                                        if(item.type === 'folder') {
                                            setParentID({ _id: item._id, item });
                                            navigate('/contentManager');
                                        } else {
                                            setParentID({ _id: item.parent, item });
                                            navigate('/contentManager');
                                        }
                                    }}>
                                        Open
                                    </Button>
                                }
                            >
                                <ListItemText
                                    primary={item.filename}
                                    secondary={`${item.type} • ${formatBytes(item.size || 0)}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>

            {!fullLoad && hasMore && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button variant="outlined" disabled={loading} onClick={() => performSearch(false)}>
                        {loading ? 'Loading...' : 'Load more'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default SearchPage;
